// apps/merchant-v3/src/lib/i18n/config.ts
//
// Phase 6 Wave 2 Task 06 — i18next bootstrap. Three locales (VI primary
// per project rules, EN secondary, FR placeholder). Detection order:
// cookie → localStorage → user.language Zustand → navigator → fallback 'vi'.
//
// Namespaces: common (this task), auth, dashboard, products, orders,
// stores, wallet, billing, notifications, settings, branding, errors.
// Per-module namespaces ship with their respective waves; this file
// pre-registers the namespace list so callers can import t('orders:...')
// once the bundles land.
//
// vg-binding: PLAN:task-06
// vg-binding: PLAN:task-07
// vg-binding: P6.D-131
// vg-binding: P6.D-221
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Phase 5.2 Wave 5 Task 17 — settings-api-keys namespace (Partner Order API
// key management UI, Task 16). Eagerly bundled so the API Keys settings tab
// renders localized copy on first paint. 3 locales, identical key sets.
// vg-binding: P5.2.D-20
// vg-binding: P5.2.D-21
//
// Phase 6.1 Wave 5 Task 20 — members / teams / teamDetail namespaces
// (Members + Teams + Team Detail settings pages, Wave-5 FE tasks 21-24).
// 3 locales, identical key sets; enum VALUES (role/status) stay English,
// only LABELS localized. Registered eagerly so the settings pages render
// localized copy on first paint.
// vg-binding: P6.1.D-28
// vg-binding: P6.1.D-20
// vg-binding: INTERFACE-STANDARDS:error-shape

import i18next, { type i18n as I18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import frCommon from './locales/fr/common.json';
import viCommon from './locales/vi/common.json';
// Phase 6 Wave 3 Task 07 — products namespace eagerly bundled so the
// Catalog Browse page renders translated copy on first paint without a
// secondary fetch. Other namespaces will follow the same pattern when
// their owning waves land.
import enProducts from './locales/en/products.json';
import frProducts from './locales/fr/products.json';
import viProducts from './locales/vi/products.json';
import enCatalog from './locales/en/catalog.json';
import frCatalog from './locales/fr/catalog.json';
import viCatalog from './locales/vi/catalog.json';
import enSupport from './locales/en/support.json';
import frSupport from './locales/fr/support.json';
import viSupport from './locales/vi/support.json';
import enNotifChannels from './locales/en/notifChannels.json';
import frNotifChannels from './locales/fr/notifChannels.json';
import viNotifChannels from './locales/vi/notifChannels.json';
import enSettingsApiKeys from './locales/en/settings-api-keys.json';
import frSettingsApiKeys from './locales/fr/settings-api-keys.json';
import viSettingsApiKeys from './locales/vi/settings-api-keys.json';
import enBilling from './locales/en/billing.json';
import frBilling from './locales/fr/billing.json';
import viBilling from './locales/vi/billing.json';
import enMembers from './locales/en/members.json';
import frMembers from './locales/fr/members.json';
import viMembers from './locales/vi/members.json';
import enTeams from './locales/en/teams.json';
import frTeams from './locales/fr/teams.json';
import viTeams from './locales/vi/teams.json';
import enTeamDetail from './locales/en/teamDetail.json';
import frTeamDetail from './locales/fr/teamDetail.json';
import viTeamDetail from './locales/vi/teamDetail.json';
// Phase 14.1 Wave 8 Task 40 + Phase 4.12 Wave 3 Task 12 — `orders` namespace.
// Carries BOTH the embroidery 2-step accept dialog copy (EmbAcceptDialog) and
// the bulk-pay partial-success copy (summary + LOCALIZED per-row reason chips).
// Eagerly bundled so neither the fee-accept dialog nor a skipped bulk-pay row
// flashes a raw key/enum token while a lazy namespace fetch is in flight.
// vg-binding: P14.1.D-04
// vg-binding: P14.1.D-18
// vg-binding: CONTEXT:decisions=P4.12.D-29,P4.12.D-32,P4.12.D-33
// vg-binding: INTERFACE-STANDARDS:error-shape
import enOrders from './locales/en/orders.json';
import frOrders from './locales/fr/orders.json';
import viOrders from './locales/vi/orders.json';
// Phase 14.1 Wave 9 Task 46 — pricing namespace (LiveCostCalculator breakdown,
// rush toggle, service picker, packing-group summary, price error copy).
// Eagerly bundled so the cost calculator renders localized copy on first paint
// and re-renders on locale switch. 3 locales, identical key sets.
// vg-binding: P14.1.D-04
// vg-binding: P14.1.D-19
// vg-binding: INTERFACE-STANDARDS:error-shape
import enPricing from './locales/en/pricing.json';
import frPricing from './locales/fr/pricing.json';
import viPricing from './locales/vi/pricing.json';
// Phase 4.12 Wave 4 Task 16 — `checkoutPricing` (QuoteStaleBanner re-consent
// copy: title/body per QUOTE_STALE reason, reason LABELS, delta-table row
// labels, SHIPPING_UNAVAILABLE variant) and `errors` (merchant-v3 MIRROR of the
// BE catalog in `packages/i18n/locales/*/errors.json`, task-09 — the banner
// resolves `error.i18n_key` VERBATIM against it, so its keys stay FLAT and
// dotted and callers pass `keySeparator: false`).
//
// Both bundles are registered eagerly: a 409 QUOTE_STALE arrives mid-checkout
// and the banner announces a PRICE CHANGE through `role="alert"` — it must not
// flash a raw key while a lazy namespace fetch is in flight. 3 locales,
// identical key sets (P4.12.D-22).
//
// vg-binding: CONTEXT:decisions=P4.12.D-17,P4.12.D-20,P4.12.D-22
// vg-binding: P4.12.D-17
// vg-binding: P4.12.D-20
// vg-binding: P4.12.D-22
// vg-binding: INTERFACE-STANDARDS:error-shape
import enCheckoutPricing from './locales/en/checkoutPricing.json';
import frCheckoutPricing from './locales/fr/checkoutPricing.json';
import viCheckoutPricing from './locales/vi/checkoutPricing.json';
import enErrors from './locales/en/errors.json';
import frErrors from './locales/fr/errors.json';
import viErrors from './locales/vi/errors.json';

/** Canonical lower-case ISO codes used internally by i18next. */
export type Locale = 'vi' | 'en' | 'fr';

/** Display labels for the TopBar dropdown (mapped to user.language `EN/VI/FR`). */
export const LOCALES: ReadonlyArray<{
  code: Locale;
  user: 'EN' | 'VI' | 'FR';
  label: string;
  flag: string;
}> = [
  { code: 'vi', user: 'VI', label: 'Tiếng Việt', flag: 'VI' },
  { code: 'en', user: 'EN', label: 'English', flag: 'EN' },
  { code: 'fr', user: 'FR', label: 'Français', flag: 'FR' },
];

/** All namespaces planned for the merchant portal. `common` is the only
 *  bundle eagerly loaded — others lazy-load via `i18next.loadNamespaces()`
 *  when their owning wave calls `useTranslation('orders')` etc. */
export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'products',
  'orders',
  'stores',
  'wallet',
  'billing',
  'notifications',
  'settings',
  'settings-api-keys',
  'members',
  'teams',
  'teamDetail',
  'pricing',
  'branding',
  'errors',
  'checkoutPricing',
] as const;

const VITE_DEFAULT_LANG = (() => {
  // Vite-style env access without importing types — safe at module load.
  const env =
    typeof globalThis !== 'undefined'
      ? (globalThis as { import?: { meta?: { env?: Record<string, string> } } })
          .import?.meta?.env
      : undefined;
  const fromEnv = env?.VITE_DEFAULT_LANG;
  if (fromEnv === 'en' || fromEnv === 'vi' || fromEnv === 'fr') return fromEnv;
  return 'vi';
})();

/** Map user.language ('EN'|'VI'|'FR') ↔ i18next locale ('en'|'vi'|'fr'). */
export function userLangToLocale(lang: 'EN' | 'VI' | 'FR' | undefined): Locale {
  if (lang === 'EN') return 'en';
  if (lang === 'FR') return 'fr';
  return 'vi';
}

export function localeToUserLang(loc: Locale): 'EN' | 'VI' | 'FR' {
  if (loc === 'en') return 'EN';
  if (loc === 'fr') return 'FR';
  return 'VI';
}

/** Initialise i18next once; subsequent calls return the same instance. */
let inited = false;
export function initI18n(): I18n {
  if (inited) return i18next;
  inited = true;

  void i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // Resources for the `common` namespace ship eagerly. Wave 3 Task
      // 07 adds `products` to the eager bundle for the Catalog Browse
      // page; other namespaces continue to lazy-load from their waves.
      resources: {
        en: { common: enCommon, products: enProducts, catalog: enCatalog, support: enSupport, notifChannels: enNotifChannels, 'settings-api-keys': enSettingsApiKeys, billing: enBilling, members: enMembers, teams: enTeams, teamDetail: enTeamDetail, orders: enOrders, pricing: enPricing, checkoutPricing: enCheckoutPricing, errors: enErrors },
        vi: { common: viCommon, products: viProducts, catalog: viCatalog, support: viSupport, notifChannels: viNotifChannels, 'settings-api-keys': viSettingsApiKeys, billing: viBilling, members: viMembers, teams: viTeams, teamDetail: viTeamDetail, orders: viOrders, pricing: viPricing, checkoutPricing: viCheckoutPricing, errors: viErrors },
        fr: { common: frCommon, products: frProducts, catalog: frCatalog, support: frSupport, notifChannels: frNotifChannels, 'settings-api-keys': frSettingsApiKeys, billing: frBilling, members: frMembers, teams: frTeams, teamDetail: frTeamDetail, orders: frOrders, pricing: frPricing, checkoutPricing: frCheckoutPricing, errors: frErrors },
      },
      fallbackLng: VITE_DEFAULT_LANG,
      supportedLngs: ['vi', 'en', 'fr'],
      defaultNS: 'common',
      ns: ['common', 'products', 'catalog', 'support', 'notifChannels', 'settings-api-keys', 'billing', 'members', 'teams', 'teamDetail', 'orders', 'pricing', 'checkoutPricing', 'errors'],
      // FR is a placeholder — fall through to EN, then VI.
      fallbackNS: ['common'],
      interpolation: {
        escapeValue: false, // React already escapes
      },
      detection: {
        order: ['cookie', 'localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage', 'cookie'],
        lookupCookie: 'merchant-v3.lang',
        lookupLocalStorage: 'merchant-v3.lang.v1',
      },
      returnNull: false,
      returnEmptyString: false,
    });

  syncDocumentLang(i18next);

  return i18next;
}

/**
 * Keep `<html lang>` in step with the active locale.
 *
 * Phase 4.12 review finding F-04: switching the topbar language re-rendered
 * every string correctly, but `document.documentElement.lang` stayed at its
 * server-rendered value — so a screen reader announced English checkout copy
 * with Vietnamese pronunciation rules, and translation tooling mis-detected the
 * page. Nothing under `apps/merchant-v3/src`, `apps/admin/src`,
 * `apps/vendor/src` or `packages/ui/src` assigned that attribute at all.
 *
 * Runs once at init for the detected locale, then on every `languageChanged`.
 * Guarded for non-DOM environments (SSR, vitest node pool).
 */
function syncDocumentLang(instance: I18n): void {
  if (typeof document === 'undefined') return;

  const apply = (lng: string | undefined): void => {
    // `resolvedLanguage` collapses region subtags (`en-US` → `en`) and honours
    // the fallback chain, so it is what the user is actually reading.
    const active = instance.resolvedLanguage ?? lng;
    if (active) document.documentElement.lang = active;
  };

  apply(instance.language);
  instance.on('languageChanged', apply);
}

export { i18next };
